import React, { useEffect, useRef, useState } from "react";
import { Avatar, Form, Input, Select, Button, Skeleton, Tag, Switch } from "antd";
import { UserOutlined, PhoneOutlined, EnvironmentOutlined, EditOutlined, LockOutlined, LogoutOutlined } from "@ant-design/icons";
import useUser from "../../hooks/useUser";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import useData from "../../datas/useData";
import AppShell from "../../components/app-shell/AppShell";
import ChangePasswordSheet from "../../components/app-shell/ChangePasswordSheet";
import { useSession } from "../../lib/session";
import "./member.css";

const { Option } = Select;

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const PROFESSIONS = [
    "Student", "Software Engineer / Developer", "Doctor / Physician",
    "Nurse / Medical Staff", "Engineer (Civil / Mechanical / Electrical)",
    "Architect", "Teacher / Lecturer / Professor", "Banker / Financial Analyst",
    "Accountant / Auditor", "Lawyer / Legal Advisor", "Journalist / Media Professional",
    "Photographer / Videographer", "Graphic Designer / UI/UX Designer",
    "Marketing / Sales Professional", "HR / Recruitment Specialist",
    "Business Owner / Entrepreneur", "Freelancer / Consultant",
    "Farmer / Agriculture", "Garments / Textile Industry", "Driver / Transport",
    "Police / Military / Defense", "Government Service", "NGO / Development Worker",
    "Imam / Religious Leader", "Chef / Cook", "Shopkeeper / Retailer",
    "Electrician / Plumber / Technician", "Travel Agent", "Real Estate Agent",
    "Pharmacist", "Researcher / Scientist", "Social Worker / Activist",
    "Artist / Musician / Performer", "Athlete / Sports Person",
    "Housewife / Homemaker", "Retired", "Unemployed", "Other",
];

const COUNTRIES = [
    "Australia", "Bahrain", "Belgium", "Brazil", "Canada", "China",
    "Denmark", "Egypt", "Finland", "France", "Germany", "Greece",
    "Hong Kong", "India", "Indonesia", "Iran", "Iraq", "Italy",
    "Japan", "Jordan", "Kuwait", "Libya", "Malaysia", "Maldives",
    "Morocco", "Netherlands", "New Zealand", "Norway", "Oman",
    "Pakistan", "Philippines", "Portugal", "Qatar", "Russia",
    "Saudi Arabia", "Singapore", "South Africa", "South Korea",
    "Spain", "Sri Lanka", "Sweden", "Switzerland", "Thailand",
    "Turkey", "UAE", "UK", "USA", "Ukraine", "Vietnam", "Other",
];

const MyProfile = () => {
    const { logout } = useSession();
    const [passwordOpen, setPasswordOpen] = useState(false);
    const { getUserProfile, updateUserProfile } = useUser();
    const { districts, upuzilas } = useData();
    const [editing, setEditing] = useState(false);
    const [upuzilaList, setUpuzilaList] = useState([]);
    const [livingAbroad, setLivingAbroad] = useState(false);
    const [form] = Form.useForm();
    const queryClient = useQueryClient();
    const appliedProfileKeyRef = useRef("");

    const { data: profile, isLoading } = useQuery({
        queryKey: ["myProfile"],
        queryFn: getUserProfile,
    });

    useEffect(() => {
        if (!profile) return;
        const profileKey = JSON.stringify({
            id: profile?._id || "",
            updatedAt: profile?.updatedAt || "",
            district: profile?.district || "",
            livingAbroad: !!profile?.livingAbroad,
        });
        if (appliedProfileKeyRef.current === profileKey) return;
        appliedProfileKeyRef.current = profileKey;

        form.setFieldsValue(profile);
        setLivingAbroad(!!profile.livingAbroad);
        if (profile.district) {
            const list = upuzilas
                .filter((x) => String(x.district).toLowerCase() === String(profile.district).toLowerCase())
                .map((x) => String(x.upazila).replace("Upazila", "").trim());
            setUpuzilaList(list);
        }
    }, [profile, form, upuzilas]);

    const { mutate: saveProfile, isPending: saving } = useMutation({
        mutationFn: updateUserProfile,
        onSuccess: () => {
            toast.success("Profile updated!");
            setEditing(false);
            queryClient.invalidateQueries({ queryKey: ["myProfile"] });
        },
        onError: () => toast.error("Failed to save. Try again."),
    });

    const handleDistrictChange = (val) => {
        const list = upuzilas
            .filter((x) => String(x.district).toLowerCase() === val.toLowerCase())
            .map((x) => String(x.upazila).replace("Upazila", "").trim());
        setUpuzilaList(list);
        form.setFieldsValue({ upuzila: undefined });
    };

    const handleCancel = () => {
        setEditing(false);
        form.setFieldsValue(profile);
        setLivingAbroad(!!profile?.livingAbroad);
    };

    const toggleRow = (name, label, hint) => (
        <div className="profile-toggle">
            <span className="profile-toggle__text">
                <span className="profile-toggle__label">{label}</span>
                {hint ? <span className="profile-toggle__hint">{hint}</span> : null}
            </span>
            <Form.Item name={name} valuePropName="checked" noStyle>
                <Switch disabled={!editing} />
            </Form.Item>
        </div>
    );

    if (isLoading) {
        return (
            <AppShell title="Profile">
                <div className="app-card">
                    <Skeleton avatar active paragraph={{ rows: 4 }} />
                </div>
            </AppShell>
        );
    }

    const saveBar = editing ? (
        <div className="profile-save-bar">
            <Button size="large" onClick={handleCancel}>
                Cancel
            </Button>
            <Button size="large" type="primary" loading={saving} onClick={() => form.submit()}>
                Save changes
            </Button>
        </div>
    ) : null;

    return (
        <AppShell
            title="Profile"
            footer={saveBar}
            hideTabs={editing}
            actions={
                editing ? null : (
                    <Button type="text" icon={<EditOutlined />} onClick={() => setEditing(true)}>
                        Edit
                    </Button>
                )
            }
        >
            <div className="app-card profile-hero">
                <Avatar className="profile-hero__avatar" size={84} src={profile?.profilePhoto || undefined}>
                    {profile?.name ? profile.name.charAt(0).toUpperCase() : <UserOutlined />}
                </Avatar>
                <div className="profile-hero__name">{profile?.name || "—"}</div>
                {profile?.profession ? <div className="profile-hero__sub">{profile.profession}</div> : null}
                <div className="profile-hero__tags">
                    {profile?.blood ? <Tag color="red">{profile.blood}</Tag> : null}
                    {profile?.district ? (
                        <Tag icon={<EnvironmentOutlined />} color="green">
                            {profile.district}
                        </Tag>
                    ) : null}
                    {profile?.livingAbroad && profile?.country ? <Tag color="blue">🌍 {profile.country}</Tag> : null}
                    <Tag color={profile?.available ? "green" : "orange"}>
                        {profile?.available ? "Available to donate" : "Unavailable"}
                    </Tag>
                </div>
            </div>

            <Form
                form={form}
                layout="vertical"
                className="profile-form"
                onFinish={(values) => saveProfile({ ...values, livingAbroad })}
            >
                <div className="app-section-title">Blood donation</div>
                <div className="app-card">
                    <Form.Item label="Blood group" name="blood">
                        <Select size="large" disabled={!editing} placeholder="Select blood group">
                            {BLOOD_GROUPS.map((b) => (
                                <Option key={b} value={b}>
                                    {b}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    {toggleRow("available", "Available to donate", "Shown on your profile to other members")}
                    {toggleRow(
                        "is_contact_public",
                        "Show my phone number",
                        "Logged-in members can call you from Find Blood and Members"
                    )}
                </div>

                <div className="app-section-title">Basic info</div>
                <div className="app-card">
                    <Form.Item label="Full name" name="name">
                        <Input size="large" prefix={<UserOutlined />} disabled={!editing} placeholder="Your name" />
                    </Form.Item>
                    <Form.Item label="Phone" name="phone">
                        <Input size="large" prefix={<PhoneOutlined />} disabled placeholder="Phone number" />
                    </Form.Item>
                    <Form.Item label="Alternate mobile" name="mobile1">
                        <Input size="large" inputMode="tel" disabled={!editing} placeholder="Alternate number" />
                    </Form.Item>
                </div>

                <div className="app-section-title">Location</div>
                <div className="app-card">
                    <Form.Item label="District" name="district">
                        <Select
                            size="large"
                            showSearch
                            disabled={!editing}
                            placeholder="Select district"
                            onChange={handleDistrictChange}
                        >
                            {districts.map((d) => (
                                <Option key={d._id} value={d.name}>
                                    {d.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Upazila" name="upuzila">
                        <Select size="large" showSearch disabled={!editing} placeholder="Select upazila">
                            {upuzilaList.map((u, i) => (
                                <Option key={i} value={u}>
                                    {u}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Present address" name="presentAddress">
                        <Input.TextArea disabled={!editing} rows={2} placeholder="Present address" />
                    </Form.Item>
                    <Form.Item label="Permanent address" name="parmanentAddress">
                        <Input.TextArea disabled={!editing} rows={2} placeholder="Permanent address" />
                    </Form.Item>
                    <div className="profile-toggle">
                        <span className="profile-toggle__label">Living outside Bangladesh</span>
                        <Switch
                            checked={livingAbroad}
                            disabled={!editing}
                            onChange={(val) => {
                                setLivingAbroad(val);
                                if (!val) form.setFieldsValue({ country: undefined });
                            }}
                        />
                    </div>
                    {livingAbroad && (
                        <Form.Item label="Country" name="country">
                            <Select size="large" showSearch disabled={!editing} placeholder="Select country">
                                {COUNTRIES.map((c) => (
                                    <Option key={c} value={c}>
                                        {c}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                </div>

                <div className="app-section-title">Work &amp; interests</div>
                <div className="app-card">
                    <Form.Item label="Profession" name="profession">
                        <Select size="large" showSearch disabled={!editing} placeholder="Select profession">
                            {PROFESSIONS.map((p) => (
                                <Option key={p} value={p}>
                                    {p}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item label="Business location" name="businessLocation">
                        <Input size="large" disabled={!editing} placeholder="City or area" />
                    </Form.Item>
                    <Form.Item label="Business location link" name="businessLocationLink">
                        <Input size="large" disabled={!editing} placeholder="Google Maps / website" />
                    </Form.Item>
                    <Form.Item label="Special skill" name="specialSkill">
                        <Input size="large" disabled={!editing} placeholder="e.g. Web Development" />
                    </Form.Item>
                    <Form.Item label="Hobby" name="hobby">
                        <Input size="large" disabled={!editing} placeholder="e.g. Photography, Cricket" />
                    </Form.Item>
                    <Form.Item label="Introduce yourself" name="introduction">
                        <Input.TextArea
                            disabled={!editing}
                            rows={3}
                            placeholder="Tell the community about yourself..."
                            maxLength={300}
                            showCount
                        />
                    </Form.Item>
                </div>

                <div className="app-section-title">Contact &amp; social</div>
                <div className="app-card">
                    <Form.Item label="Email" name="email">
                        <Input size="large" inputMode="email" disabled={!editing} placeholder="Email address" />
                    </Form.Item>
                    <Form.Item label="WhatsApp" name="whatsapp">
                        <Input size="large" inputMode="tel" disabled={!editing} placeholder="WhatsApp number" />
                    </Form.Item>
                    <Form.Item label="Facebook" name="facebook">
                        <Input size="large" disabled={!editing} placeholder="facebook.com/yourprofile" />
                    </Form.Item>
                    <Form.Item label="LinkedIn" name="linkedin">
                        <Input size="large" disabled={!editing} placeholder="linkedin.com/in/yourprofile" />
                    </Form.Item>
                </div>
            </Form>

            {!editing ? (
                <>
                    <Button
                        className="profile-password"
                        size="large"
                        block
                        icon={<LockOutlined />}
                        onClick={() => setPasswordOpen(true)}
                    >
                        Change password
                    </Button>
                    <Button className="profile-logout" size="large" block icon={<LogoutOutlined />} onClick={logout}>
                        Log out
                    </Button>
                </>
            ) : null}
            <ChangePasswordSheet open={passwordOpen} onClose={() => setPasswordOpen(false)} />
        </AppShell>
    );
};

export default MyProfile;
